import React, { useMemo, useRef, useState } from 'react';
import { BsAt, BsTrash } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Flex, HStack, Icon, IconButton, Text } from '@chakra-ui/react';
import { Parser } from 'html-to-react';
import PropTypes from 'prop-types';

import RichTextEditor from '../../../../../../../../../components/RichTextEditor';
import { deleteCommentSubComment, updateCommentSubComment } from '../../../../../../../../../redux/modules/activityLog';
import { hideModal } from '../../../../../../../../../redux/modules/modal';
import { selectUser } from '../../../../../../../../../redux/modules/user';
import { ContainerDimensions } from '../../../../../../../../../styles/layoutDimensions';
import { formatActivityTime } from '../../../../../../../../../utils/activityTimeUtil';
import { showDontAskAgainModal } from '../../../../../../../../../utils/askAgainModalUtil';
import ActivityLogUserIcon from '../ActivityLogUserIcon';
import { parseTaggedUsersToComment } from '../util';

const ActivityLogSubRow = ({ comment }) => {
  const dispatch = useDispatch();
  const firstName = comment?.user?.userFirstName || comment.userFirstName || comment?.user?.firstName;
  const lastName = comment?.user?.userLastName || comment?.userLastName || comment?.user?.lastName;
  const { message, updatedAt: activityTime, id, user, commentTags: tags } = comment;

  const htmlParser = new Parser();
  const parsedComment = htmlParser.parse(message);
  const editSubCommentRef = useRef();
  const [isEditingComment, setIsEditingComment] = useState(false);
  const [editCommentMessage, setEditCommentMessage] = useState(message);

  const currentUser = useSelector(selectUser);

  const userCanEdit = useMemo(() => currentUser.userId === comment.userId, [currentUser, user]);

  const parsedTags = useMemo(() => {
    if (!tags || !tags?.length) {
      return null;
    }

    const mappedTags = tags.map(tag => ({
      id: tag?.id ?? tag.userInfo.userId,
      description: tag?.description ?? `${tag.userInfo.userFirstName} ${tag.userInfo.userLastName}`,
      userEmail: tag?.userEmail ?? tag.userInfo.userEmail
    }));

    return parseTaggedUsersToComment(mappedTags);
  }, [tags]);

  return (
    <Box ml={16} mt={2}>
      <Flex
        justifyContent="flex-start"
        maxW={ContainerDimensions.maxWidth}
        ml={6}
        mr={10}
        my={4}
      >
        <Box mr={6}>
          <ActivityLogUserIcon firstName={firstName} lastName={lastName} />
        </Box>
        <Box textAlign="left" w="100%">
          <Flex justifyContent="space-between">
            <Text textStyle="bodyLg500">{`${firstName} ${lastName}`}</Text>
            <Text color="greyScale.400" textAlign="right" textStyle="bodyMd">
              {formatActivityTime(activityTime)}
            </Text>
          </Flex>
          {isEditingComment ? (
            <Flex
              justifyContent="flex-start"
              maxW={ContainerDimensions.maxWidth}
              ml={6}
              mr={10}
              my={4}
            >
              <RichTextEditor
                id={`edit-comment-text-editor_${comment.id}`}
                innerRef={editSubCommentRef}
                value={editCommentMessage}
                onChange={value => setEditCommentMessage(value)}
              />
            </Flex>
          ) : (
            <Box textStyle="bodyMd">{parsedComment}</Box>
          )}

          <Box my={2}>
            { !isEditingComment && parsedTags
              && (
                <HStack>
                  <Icon as={BsAt} boxSize={6} color="greyScale.400" />
                  <Text color="primaryScale.600" fontSize="xs">{parsedTags}</Text>
                </HStack>
              )}
          </Box>

          <HStack justifyContent="space-between">
            <Box>
              {!isEditingComment && userCanEdit && (
                <HStack>
                  <Button
                    variant="textPrimary"
                    onClick={() => setIsEditingComment(!isEditingComment)}
                  >
                    Edit
                  </Button>
                  <IconButton
                    aria-label="Delete"
                    icon={<Icon as={BsTrash} />}
                    size="sm"
                    variant="textGrey"
                    onClick={() => showDontAskAgainModal({
                      dispatch,
                      cookieId: 'delete-comment-confirm',
                      description: 'You cannot undo this action.',
                      onClick: () => {
                        dispatch(deleteCommentSubComment(comment.commentable.id, id));
                        dispatch(hideModal());
                      },
                      title: 'Delete Comment?'
                    })}
                  />
                </HStack>
              )}
            </Box>

            {isEditingComment && (
              <HStack>
                <Button
                  onClick={() => {
                    const parentId = comment.commentable.id;
                    const parentType = comment.commentableType;

                    dispatch(updateCommentSubComment(parentId, parentType, id, editCommentMessage));
                    setIsEditingComment(!isEditingComment);
                  }}
                >
                  Save
                </Button>
                <Button
                  variant="textPrimary"
                  onClick={() => setIsEditingComment(!isEditingComment)}
                >
                  Cancel
                </Button>
              </HStack>
            )}
          </HStack>
        </Box>
      </Flex>
    </Box>
  );
};

ActivityLogSubRow.propTypes = {
  comment: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    user: PropTypes.shape({
      userFirstName: PropTypes.string,
      userLastName: PropTypes.string
    }),
    commentable: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired }),
    commentableType: PropTypes.string,
    userFirstName: PropTypes.string,
    userLastName: PropTypes.string,
    message: PropTypes.string,
    updatedAt: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    commentTags: PropTypes.arrayOf(PropTypes.shape({}))
  })
};

export default ActivityLogSubRow;
